import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { tableStyle, thStyle, tdStyle, inputStyle, btnSecondary } from './CommissionOverview';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const AUDITABLE_TYPES = ['ComEntry', 'ComAdjustment', 'ComSettlement', 'ComSettlementBatch', 'ComAgreement', 'ComRule', 'ComProfile', 'ComBeneficiary'];
const CATEGORIES = ['calculation', 'financial', 'override', 'applicability', 'settings'];

export default function CommissionHistory() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [auditableType, setAuditableType] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/commissions/history?per_page=50`;
      if (auditableType) url += `&auditable_type=${auditableType}`;
      if (category) url += `&category=${category}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, auditableType, category, search, dateFrom, dateTo]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/history/export`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'commission_audit_history.json';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🕒 Commission Audit History</h2>
        <button style={btnSecondary} onClick={doExport} disabled={exporting}>{exporting ? 'Exporting...' : '⬇ Export'}</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, width: 200 }} />
        <select value={auditableType} onChange={e => setAuditableType(e.target.value)} style={{ ...inputStyle, width: 170 }}>
          <option value="">All Record Types</option>
          {AUDITABLE_TYPES.map(t => <option key={t} value={t}>{t.replace('Com', '')}</option>)}
        </select>
        <select value={category} onChange={e => setCategory(e.target.value)} style={{ ...inputStyle, width: 150 }}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ ...inputStyle, width: 150 }} />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ ...inputStyle, width: 150 }} />
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Date', 'Record', 'Action', 'Category', 'Old Value', 'New Value', 'Reason', 'User'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No audit records yet</td></tr>}
              {list.map(l => (
                <tr key={l.id}>
                  <td style={tdStyle}>{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</td>
                  <td style={tdStyle}>{(l.auditable_type || '').replace('Com', '')} #{l.auditable_id}</td>
                  <td style={tdStyle}>{(l.action || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{l.category || '—'}</td>
                  <td style={{ ...tdStyle, maxWidth: 160, whiteSpace: 'normal', wordBreak: 'break-word' }}>{formatVal(l.old_value)}</td>
                  <td style={{ ...tdStyle, maxWidth: 160, whiteSpace: 'normal', wordBreak: 'break-word' }}>{formatVal(l.new_value)}</td>
                  <td style={{ ...tdStyle, maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word' }}>{l.reason || '—'}</td>
                  <td style={tdStyle}>{l.user?.name || 'System'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function formatVal(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
